import { useContext, useEffect, useState } from 'react';
import ChildNameHeading from 'components/SiblingSwitch/ChildNameHeading';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardProps,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { useNavigate } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';

import { getInsuranceDetails } from 'store/slices/userSlice';

import { useAppDispatch } from 'store/hooks';
import { AuthContext } from 'utils/AuthContext';
import { ROUTE } from 'route/RouteEnums';
import { getAddCardLink } from 'store/slices/appointmentSlice';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { chargePaymentSchema } from 'utils/schema';
import { chargePayment } from 'store/slices/paymentSlice';
import PrimaryCard from '../InsuranceDetails/PrimaryCard';
import Payments from 'pages/Admin/PatientInfo/Payments';
import RequestBalanceDialog from './RequestBalanceDialog';
import ProfileCompletionGuard from './ProfileCompletionGuard';
import { StyledDialog } from 'theme/index';
import { validatePaymentStatus } from 'store/slices/patientInfoSlice';

/** Vertical layout of each Billing tile's content (shared by all tiles). */
const TILE_CONTENT_JUSTIFY = 'space-between';

const Billing = (props: any) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);

  const [primaryInsuranceData, setPrimaryInsuranceData] = useState<any>(null);
  const [secondaryInsuranceData, setSecondaryInsuranceData] = useState<any>(null);
  const [showMakePaymentModal, setShowMakePaymentModal] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState<boolean>(false);
  /** MM-1130: Request Your Balance dialog visibility */
  const [showRequestBalance, setShowRequestBalance] = useState<boolean>(false);
  const patientId = user?.patientId;
  const [creditCardStatus, setCreditCardStatus] = useState<string>('');
  const [showPaymentConfirm, setShowPaymentConfirm] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: yupResolver(chargePaymentSchema),
    mode: 'all',
  });

  const loadInsuranceDetails =() => {
    if (!patientId) return;
    setPrimaryInsuranceData(null);
    setSecondaryInsuranceData(null);
    dispatch(getInsuranceDetails(patientId)).unwrap().then((response: any) => {
      if (response?.status === 200) {
        const data = response?.data;
        if (data?.payOutOfPocket) {
          return;
        }
        setPrimaryInsuranceData(data?.primaryInsurance || null);
        setSecondaryInsuranceData(data?.secondaryInsurance || null);
      }
    });
  };

  useEffect(() => {
    loadInsuranceDetails();
    getPaymentStatusData();
  }, [patientId]);


  const StyleCard = styled(Card)<CardProps>(({ theme }) => ({
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
    border: '1px solid #EFF4FF',
    borderRadius: 12,
    background: '#FFF',
    margin: '12px 16px',
    position: 'relative',
    zIndex: 99,
    padding: 20,
    '& .MuiTypography-root': {
      position: 'relative',
    },
    '& .MuiCardContent-root:last-child': {
      padding: 0,
    },
    /**
     * MM-1132 feedback: tile titles are 17px (was 20px).
     */
    '.MuiTypography-h2': {
      fontSize: 17,
    },
  }));

  const editPrimaryInsurance = () => {
    navigate(`${ROUTE.INSURANCE_DETAILS}`, { state: { primary: true } });
  };

  const handleViewCreditCard = () => {
    setShowPaymentConfirm(false);
    dispatch(getAddCardLink({}))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          setTimeout(() => {
            window.open(response?.data?.url, '_blank');
          });
        }
      });
  };

  const handleProceedToPay = () => {
    setShowMakePaymentModal(true);
  };

  const getPaymentStatusData = () => {
    if (!patientId) return;
    try {
      dispatch(validatePaymentStatus(user.userId)).unwrap().then((response: any) => {
        if (response?.status === 200) {
          const data = response?.data;
          const status = data?.status ;
          const expired =  data?.cardExpired;
          if(status==='No') {
            setCreditCardStatus('No Payment method on file');
          } else if (status === 'Yes' && !expired) {
            setCreditCardStatus('Payment method on file' );
          } else if (status === 'Yes' && expired) {
            setCreditCardStatus('Payment method on file has expired' );
          }
        }
      });
    } catch (error) {
      console.error('Error fetching payment status:', error);
    }
  };

  const handleShowPaymentHistory = () => {
    setShowPaymentHistory(true);
  };

  const handlePayNow = (formData: any) => {
    setDisableButton(true);
    const data = {
      description: formData.description,
      amount: formData.amount,
      chargeType: 'SELF_PAYMENT',
      patientId: patientId,
    };
    dispatch(chargePayment(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setShowMakePaymentModal(false);
          toast(`Your payment of $${watch('amount')} has been processed successfully. Thank you!`, {
            type: 'success',
            icon: true,
          });
        }
      })
      .catch((e: any) => {
        if (e.err.errorCode === 8001) {
          toast('No valid payment available.', {
            type: 'error',
            icon: true,
          });
        } else if (e.err.errorCode === 8002) {
          toast('Payment Could Not Be Processed.', {
            type: 'error',
            icon: true,
          });
        }
        setShowMakePaymentModal(false);
      });
  };

  return (
    <Container component='main' maxWidth='md'>
      <ProfileCompletionGuard />
      <Grid container>
        <Grid item xs={10} display={'flex'} alignItems={'center'}>
          {/* MM-1123: same top space above the heading as the Dashboard (paddingTop 6) */}
          <Typography variant='h5' gutterBottom sx={{ paddingTop: 6 }}>
            {/* MM-1123 / MM-1130: child's name with yellow underline, e.g. "Emma’s Billing" */}
            <ChildNameHeading title='Billing' />
          </Typography>
        </Grid>
        {/* MM-1130: Request Your Balance tile, immediately above Make a Payment */}
        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={TILE_CONTENT_JUSTIFY}
                  gap={1.5}
                >
                  <Typography variant='h2'>Request Your Balance</Typography>
                  <Grid container>
                    <Grid item xs={12} sm>
                      <Typography variant='subtitle1'>
                        Submit a request to find out your outstanding balance. We’ll respond within one business
                        day by text message or MindWeal Secure Chat.
                      </Typography>
                    </Grid>
                    {/* MM-1130 feedback: on phones the button goes under the text so its label is not cut */}
                    <Grid
                      item
                      xs={12}
                      sm='auto'
                      alignItems={'end'}
                      textAlign={'end'}
                      mt={{ xs: 1, sm: 0 }}
                      pl={{ sm: 2 }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        // MM-1130 feedback: keep "REQUEST BALANCE" on one line (no 125px cap like shorter labels)
                        sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                        onClick={() => setShowRequestBalance(true)}
                      >
                        REQUEST BALANCE
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>

        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={TILE_CONTENT_JUSTIFY}
                  gap={1.5}
                >
                  <Typography variant='h2'>Make a Payment</Typography>
                  <Grid container>
                    <Grid item xs={12} sm>
                      <Typography variant='subtitle1'>
                        Easily pay your outstanding balance.
                      </Typography>
                    </Grid>
                    {/* MM-1132 feedback: on phones the link goes under the text, like Request Your Balance */}
                    <Grid
                      item
                      xs={12}
                      sm='auto'
                      alignItems={'end'}
                      textAlign={'end'}
                      mt={{ xs: 1, sm: 0 }}
                      pl={{ sm: 2 }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                        onClick={handleProceedToPay}
                      >
                        PROCEED TO PAY
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>

        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={TILE_CONTENT_JUSTIFY}
                  gap={1.5}
                >
                  <Typography variant='h2'>Payment History</Typography>
                  <Grid container>
                    <Grid item xs={12} sm>
                      <Typography variant='subtitle1'>Check your payment transactions.</Typography>
                    </Grid>
                    {/* MM-1132 feedback: on phones the link goes under the text, like Request Your Balance */}
                    <Grid
                      item
                      xs={12}
                      sm='auto'
                      alignItems={'end'}
                      textAlign={'end'}
                      mt={{ xs: 1, sm: 0 }}
                      pl={{ sm: 2 }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                        onClick={handleShowPaymentHistory}
                      >
                        VIEW DETAILS
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>

        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={TILE_CONTENT_JUSTIFY}
                  gap={1.5}
                >
                  <Typography variant='h2'>Credit Card Details</Typography>
                  {creditCardStatus === 'Payment method on file' ? (
                    <Typography sx={{ color: 'green', fontWeight: 700 }}>
                        Payment method on file
                    </Typography>
                  ) : ( <Typography sx={{ color: '#F04438', fontWeight: 700 }}>
                    {creditCardStatus}
                  </Typography>)}
                      
                  <Grid container>
                    <Grid item xs={12} sm>
                      <Typography variant='subtitle1'>
                          Update and manage your payment & card details here.
                      </Typography>
                    </Grid>
                    {/* MM-1132 feedback: on phones the link goes under the text, like Request Your Balance */}
                    <Grid
                      item
                      xs={12}
                      sm='auto'
                      alignItems={'end'}
                      textAlign={'end'}
                      mt={{ xs: 1, sm: 0 }}
                      pl={{ sm: 2 }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                        onClick={() => { setShowPaymentConfirm(true); }}
                      >
                          VIEW DETAILS
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>
      </Grid>
      {/* MM-1130: Additional Billing Questions dialog for Request Your Balance */}
      <RequestBalanceDialog
        open={showRequestBalance}
        patientId={patientId}
        onClose={() => setShowRequestBalance(false)}
      />
      <StyledDialog open={showMakePaymentModal}>
        <Box
          width={'100%'}
          component={'form'}
          onSubmit={handleSubmit((formData) => handlePayNow(formData))}
        >
          <IconButton
            aria-label='close'
            onClick={() => setShowMakePaymentModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <Grid container spacing={4} display={'flex'}>
              <Grid item xs={12} alignItems={'center'} textAlign={'center'}>
                <Typography variant='h2' textAlign={'center'} pb={2}>
                  Make a Payment
                </Typography>
              </Grid>
              <Grid
                width={'100%'}
                display={'flex'}
                flexDirection={'column'}
                sx={{ marginLeft: '50px' }}
              >
                <Grid alignItems={'start'}>
                  <Typography sx={{ fontWeight: 700 }}>Payment Amount*</Typography>
                  <TextField
                    id='amount'
                    fullWidth
                    placeholder='Amount Due'
                    inputProps={{ maxLength: 6 }}
                    InputProps={{
                      startAdornment: <InputAdornment position='start'>$</InputAdornment>,
                    }}
                    error={!!errors.amount}
                    helperText={errors?.amount?.message}
                    {...register('amount')}
                  />
                </Grid>
                <Grid alignItems={'start'} mt='20px'>
                  <Typography sx={{ fontWeight: 700 }}>Details of Payment*</Typography>
                  <TextField
                    id='description'
                    fullWidth
                    placeholder='Reason you are paying'
                    inputProps={{ maxLength: 250 }}
                    error={!!errors.description}
                    helperText={errors?.description?.message}
                    {...register('description')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant='contained'
              type='submit'
              disabled={!isValid || !isDirty || disableButton}
            >
              PAY NOW
            </Button>
          </DialogActions>
        </Box>
      </StyledDialog>
      <StyledDialog
        open={showPaymentHistory}
        disableEnforceFocus
        fullWidth
        maxWidth='sm'
      >
        {/* MM-843: close icon pinned to the corner, so there's no empty row above the title */}
        <IconButton
          aria-label='close'
          onClick={() => setShowPaymentHistory(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle
          id='simple-dialog-title'
          sx={{
            padding: '0px 24px 0',
            textAlign: 'center',
          }}
        >
          Payment History
        </DialogTitle>

        <DialogContent
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            px: { xs: 2, sm: 3 },
            boxSizing: 'border-box',
          }}
        >
          <Payments patientId={patientId} from='patient' />
        </DialogContent>
      </StyledDialog>
      {/*  -------------- credit card/ payment details grid ------------------ */}
      {primaryInsuranceData != null ? (
        <Grid item xs={12}>
          <StyleCard>
            <CardContent sx={{ position: 'relative' }}>
              <Typography variant='h2'>Insurance Details</Typography>
              <Grid container spacing={1} paddingTop={2.5}>
                <Grid item sm={6} xs={12}>
                  <PrimaryCard
                    editInsurance={editPrimaryInsurance}
                    insurancetype='Primary'
                    data={primaryInsuranceData}
                    fromadmin={true}
                  />
                </Grid>
                {secondaryInsuranceData != null && (
                  <Grid item sm={6} xs={12}>
                    <PrimaryCard
                      editInsurance={editPrimaryInsurance}
                      insurancetype='Secondary'
                      data={secondaryInsuranceData}
                      fromadmin={true}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={TILE_CONTENT_JUSTIFY}
                  gap={1.5}
                >
                  <Typography variant='h2'>Insurance</Typography>
                  <Grid container>
                    <Grid item xs={12} sm>
                      <Typography variant='subtitle1'>Add your insurance details</Typography>
                    </Grid>
                    {/* MM-1132 feedback: on phones the link goes under the text, like Request Your Balance */}
                    <Grid
                      item
                      xs={12}
                      sm='auto'
                      alignItems={'end'}
                      textAlign={'end'}
                      mt={{ xs: 1, sm: 0 }}
                      pl={{ sm: 2 }}
                    >
                      <Button
                        variant='text'
                        size='small'
                        sx={{ fontSize: '14px', padding: 0, whiteSpace: 'nowrap' }}
                        onClick={editPrimaryInsurance}
                      >
                        ADD
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </StyleCard>
        </Grid>
      )}
      <StyledDialog
        open={showPaymentConfirm}
        onClose={() => setShowPaymentConfirm(false)}
        maxWidth='xs' // ✅ smaller width
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          {' '}
          {/* ✅ reduce padding */}
          <Grid container spacing={1.5} textAlign='center'>
            {/* Title */}
            <Grid item xs={12}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
               Update Payment Method
              </Typography>
            </Grid>
      
            {/* Message */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '13px', px: 1 }}>
               Click OK to securely update your payment method on Stripe. After returning to MindWeal, you may be taken to the Profile section instead of Billing. From there, you can return to the Dashboard.
              </Typography>
            </Grid>
      
            {/* Button */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 1,
                }}
              >
                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 90, // ✅ smaller button
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                    textTransform: 'none',
                  }}
                  onClick={() => handleViewCreditCard()}
                >
                 OK
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>
    </Container>
  );
};

export default Billing;
